import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import api from '@services/api';
import {
  ContestHubHeader,
  ContestSponsorBanner,
  ContestTaskCard,
  FeaturedSmashContest,
  type ContestTaskDisplay,
} from '@components/contest/ContestHubUI';
import {
  getContestCategoryLabel,
  getContestTaskCategory,
  matchesContestTaskCategory,
  type ContestView,
} from '@utils/contest';

interface ContestSubmission {
  id: string;
  contestTaskId: string;
  videoUrl: string;
  caption?: string;
  status: string;
  score?: number | null;
  rating?: number | null;
  feedback?: string;
  createdAt?: string;
}

interface PaymentStatus {
  status: 'unpaid' | 'pending' | 'paid';
  mode?: string;
  paymentId?: string;
  entryFee?: number;
  currency?: string;
}

interface ContestTasksPageProps {
  player: any;
  sport: string;
  view?: ContestView;
  categoryFilter?: string;
  hideHeader?: boolean;
  highlightTaskId?: string;
  onViewChange?: (view: ContestView, taskId?: string) => void;
}

const ContestTasksPage: React.FC<ContestTasksPageProps> = ({
  player,
  sport,
  view = 'browse',
  categoryFilter = 'all',
  hideHeader = false,
  highlightTaskId,
  onViewChange,
}) => {
  const [tasks, setTasks] = useState<ContestTaskDisplay[]>([]);
  const [submissionsByTask, setSubmissionsByTask] = useState<Record<string, ContestSubmission[]>>({});
  const [allSubmissionsByTask, setAllSubmissionsByTask] = useState<Record<string, ContestSubmission[]>>({});
  const [filesByTask, setFilesByTask] = useState<Record<string, File | null>>({});
  const [captionsByTask, setCaptionsByTask] = useState<Record<string, string>>({});
  const [categoryByTask, setCategoryByTask] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [uploadingTaskId, setUploadingTaskId] = useState<string | null>(null);
  const [payingTaskId, setPayingTaskId] = useState<string | null>(null);
  const [paymentStatusByTask, setPaymentStatusByTask] = useState<Record<string, PaymentStatus>>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [expandedUploadTaskId, setExpandedUploadTaskId] = useState<string | null>(highlightTaskId || null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const taskRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  const loadTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/contest-tasks?sport=${encodeURIComponent(sport)}`);
      const loadedTasks: ContestTaskDisplay[] = response.data?.data || [];
      setTasks(loadedTasks);

      // Fetch player's own submissions (backend resolves player from JWT)
      const playerSubmissionPairs = await Promise.all(
        loadedTasks.map(async (task) => {
          const res = await api.get(`/contest-tasks/${task.id}/submissions`);
          return [task.id, res.data?.data || []] as const;
        })
      );
      setSubmissionsByTask(Object.fromEntries(playerSubmissionPairs));

      // Fetch payment status for each task (backend resolves player from JWT)
      const paymentPairs = await Promise.all(
        loadedTasks.map(async (task) => {
          try {
            const res = await api.get(`/contest-tasks/${task.id}/payments/status`);
            return [task.id, res.data?.data as PaymentStatus] as const;
          } catch {
            return [task.id, { status: 'unpaid' as const }] as const;
          }
        })
      );
      setPaymentStatusByTask(Object.fromEntries(paymentPairs));

      if (view === 'leaderboard') {
        // For leaderboard: fetch without auth to get all approved submissions
        const allSubmissionPairs = await Promise.all(
          loadedTasks.map(async (task) => {
            const res = await api.get(`/contest-tasks/${task.id}/submissions`);
            return [task.id, res.data?.data || []] as const;
          })
        );
        setAllSubmissionsByTask(Object.fromEntries(allSubmissionPairs));
      } else {
        setAllSubmissionsByTask({});
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e.message || 'Failed to load contest tasks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (player?.id) {
      void loadTasks();
    }
  }, [player?.id, sport, view]);

  useEffect(() => {
    if (highlightTaskId) {
      setExpandedUploadTaskId(highlightTaskId);
      const node = taskRefs.current[highlightTaskId];
      if (node) {
        node.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [highlightTaskId, tasks.length]);

  const filteredTasks = useMemo(
    () => tasks.filter((task) => matchesContestTaskCategory(task, categoryFilter)),
    [tasks, categoryFilter]
  );

  const taskTitleById = useMemo(
    () => Object.fromEntries(tasks.map((task) => [task.id, task.title])),
    [tasks]
  );

  const taskCategoryById = useMemo(
    () => Object.fromEntries(tasks.map((task) => [task.id, getContestTaskCategory(task)])),
    [tasks]
  );

  const filteredTaskIds = useMemo(() => new Set(filteredTasks.map((task) => task.id)), [filteredTasks]);

  const uploadSubmission = async (task: ContestTaskDisplay) => {
    const file = filesByTask[task.id];
    if (!file) {
      setError('Select a video file before submitting');
      return;
    }

    const formData = new FormData();
    formData.append('video', file);
    formData.append('sport', sport);
    formData.append('participantName', player.name || player.displayName || `${player.firstName || ''} ${player.lastName || ''}`.trim());
    if (categoryByTask[task.id]) formData.append('contestCategory', categoryByTask[task.id]);
    formData.append('caption', captionsByTask[task.id] || '');

    try {
      setUploadingTaskId(task.id);
      setUploadProgress(0);
      await api.post(`/contest-tasks/${task.id}/submissions`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // No global 30s timeout for video uploads — allow up to 10 minutes
        timeout: 600000,
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
          }
        },
      });
      setUploadProgress(100);
      setSuccess('Performance video submitted successfully!');
      setFilesByTask((prev) => ({ ...prev, [task.id]: null }));
      setCaptionsByTask((prev) => ({ ...prev, [task.id]: '' }));
      setCategoryByTask((prev) => ({ ...prev, [task.id]: '' }));
      await loadTasks();
    } catch (e: any) {
      const msg = e?.response?.data?.message || e.message || 'Failed to submit performance video';
      setError(e.code === 'ECONNABORTED' ? 'Upload timed out — try a shorter/smaller video file.' : msg);
    } finally {
      setUploadingTaskId(null);
      setUploadProgress(0);
    }
  };

  const mySubmissions = useMemo(() => {
    const rows: (ContestSubmission & { taskTitle: string; taskCategory: string })[] = [];
    Object.entries(submissionsByTask).forEach(([taskId, submissions]) => {
      if (!filteredTaskIds.has(taskId)) return;
      submissions.forEach((submission) => {
        rows.push({
          ...submission,
          taskTitle: taskTitleById[taskId] || 'Contest',
          taskCategory: taskCategoryById[taskId] || 'skills',
        });
      });
    });
    return rows.sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
  }, [submissionsByTask, filteredTaskIds, taskTitleById, taskCategoryById]);

  const leaderboardRows = useMemo(() => {
    const rows: (ContestSubmission & { taskTitle: string; taskCategory: string })[] = [];
    Object.entries(allSubmissionsByTask).forEach(([taskId, submissions]) => {
      if (!filteredTaskIds.has(taskId)) return;
      submissions.forEach((submission) => {
        if (submission.status !== 'approved') return;
        rows.push({
          ...submission,
          taskTitle: taskTitleById[taskId] || 'Contest',
          taskCategory: taskCategoryById[taskId] || 'skills',
        });
      });
    });
    return rows.sort((a, b) => {
      const scoreDiff = Number(b.score || 0) - Number(a.score || 0);
      if (scoreDiff !== 0) return scoreDiff;
      return Number(b.rating || 0) - Number(a.rating || 0);
    });
  }, [allSubmissionsByTask, filteredTaskIds, taskTitleById, taskCategoryById]);

  const switchView = (nextView: ContestView, taskId?: string) => {
    onViewChange?.(nextView, taskId);
  };

  const handlePayNow = async (task: ContestTaskDisplay) => {
    setPayingTaskId(task.id);
    setError(null);
    try {
      const sessionRes = await api.post(`/contest-tasks/${task.id}/payments/create`, {
        currency: 'INR',
      });
      const session = sessionRes.data?.data as {
        mode: string; paymentId: string; razorpayOrderId?: string;
        razorpayKeyId?: string; amount?: number; currency?: string;
      };

      if (session.mode === 'free' || session.mode === 'already_paid') {
        setPaymentStatusByTask((prev) => ({ ...prev, [task.id]: { status: 'paid', mode: session.mode } }));
        return;
      }

      if (session.mode === 'mock') {
        // Dev mock: auto-confirm
        await api.post(`/contest-tasks/${task.id}/payments/mock-confirm`, { paymentId: session.paymentId });
        setPaymentStatusByTask((prev) => ({ ...prev, [task.id]: { status: 'paid', mode: 'mock' } }));
        setSuccess('Payment confirmed (dev mode). You can now upload your video.');
        return;
      }

      if (session.mode === 'razorpay' && session.razorpayOrderId) {
        if (!window.Razorpay) {
          setError('Razorpay SDK not loaded. Please refresh and try again.');
          return;
        }
        const options: RazorpayOptions = {
          key: session.razorpayKeyId || '',
          amount: Math.round((session.amount ?? 0) * 100),
          currency: session.currency ?? 'INR',
          name: 'EncycGames',
          description: `Contest Entry — ${task.title}`,
          order_id: session.razorpayOrderId,
          prefill: { name: player.name || player.displayName || '' },
          theme: { color: '#0f4c81' },
          handler: async (response) => {
            try {
              await api.post(`/contest-tasks/${task.id}/payments/razorpay-verify`, {
                paymentId: session.paymentId,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              });
              setPaymentStatusByTask((prev) => ({ ...prev, [task.id]: { status: 'paid', mode: 'razorpay' } }));
              setSuccess('Payment successful! You can now upload your video.');
            } catch {
              setError('Payment completed but verification failed. Contact support with your payment ID.');
            } finally {
              setPayingTaskId(null);
            }
          },
          modal: { ondismiss: () => setPayingTaskId(null) },
        };
        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', (resp) => {
          setError(`Payment failed: ${resp.error.description ?? 'Unknown error'}`);
          setPayingTaskId(null);
        });
        rzp.open();
        return;
      }
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed to initiate payment');
    } finally {
      setPayingTaskId(null);
    }
  };

  const renderUploadSection = (task: ContestTaskDisplay & { entry_fee?: number; currency?: string }) => {
    const submissions = submissionsByTask[task.id] || [];
    const latestSubmission = submissions[0];
    const showUpload = expandedUploadTaskId === task.id;
    const payment = paymentStatusByTask[task.id];
    const entryFee = task.entry_fee ?? 0;
    const hasPaid = payment?.status === 'paid' || entryFee <= 0;

    return (
      <Box sx={{ mt: 1.5 }}>
        <Divider sx={{ mb: 1.5 }} />
        {latestSubmission && (
          <Alert
            severity={latestSubmission.status === 'approved' ? 'success' : latestSubmission.status === 'rejected' ? 'error' : 'info'}
            sx={{ mb: 1.5 }}
          >
            Latest submission: {latestSubmission.status}
            {latestSubmission.score != null ? ` | Score: ${latestSubmission.score}` : ''}
            {latestSubmission.rating != null ? ` | Rating: ${latestSubmission.rating}/5` : ''}
            {latestSubmission.feedback ? ` | Feedback: ${latestSubmission.feedback}` : ''}
          </Alert>
        )}

        {task.performanceSubmissionEnabled === false ? (
          <Alert severity="warning">Performance upload is disabled for this task.</Alert>
        ) : !showUpload ? null : !hasPaid ? (
          // ── Payment gate ──────────────────────────────────────────────────
          <Box
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: '1px solid #bfdbfe',
              bgcolor: '#eff6ff',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { sm: 'center' },
              gap: 2,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1e40af' }}>
                Entry Fee Required
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pay ₹{entryFee} {task.currency || 'INR'} to unlock video submission for this contest.
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="large"
              onClick={() => handlePayNow(task)}
              disabled={payingTaskId === task.id}
              sx={{
                bgcolor: '#1d4ed8',
                color: '#fff',
                fontWeight: 900,
                textTransform: 'none',
                borderRadius: 999,
                px: 3,
                whiteSpace: 'nowrap',
                '&:hover': { bgcolor: '#1e40af' },
              }}
            >
              {payingTaskId === task.id ? 'Processing…' : `Pay ₹${entryFee} & Submit`}
            </Button>
          </Box>
        ) : (
          // ── Upload form (shown after payment) ─────────────────────────────
          <Box>
            {hasPaid && entryFee > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
                <Typography variant="body2" color="success.main" fontWeight={700}>
                  Entry fee paid
                </Typography>
              </Box>
            )}

            <Stack spacing={1.5}>
              {/* File picker row */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  fullWidth={isMobile}
                  sx={{ textTransform: 'none', flexShrink: 0 }}
                >
                  Select Video
                  <input
                    hidden
                    type="file"
                    accept="video/*"
                    onChange={(event) => setFilesByTask((prev) => ({ ...prev, [task.id]: event.target.files?.[0] || null }))}
                  />
                </Button>
                {filesByTask[task.id] ? (
                  <Chip
                    label={filesByTask[task.id]!.name}
                    size="small"
                    onDelete={() => setFilesByTask((prev) => ({ ...prev, [task.id]: null }))}
                    sx={{ maxWidth: { xs: '100%', sm: 240 }, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                  />
                ) : (
                  <Typography variant="caption" color="text.secondary">No video selected</Typography>
                )}
              </Stack>

              {/* Category + Caption row */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  select
                  size="small"
                  label="Age / Gender Category"
                  value={categoryByTask[task.id] || ''}
                  onChange={(e) => setCategoryByTask((prev) => ({ ...prev, [task.id]: e.target.value }))}
                  sx={{ minWidth: { xs: '100%', sm: 180 } }}
                >
                  <MenuItem value="">— Select —</MenuItem>
                  {['U9', 'U11', 'U13', 'U15', 'U17', 'U19', 'Adults', 'Men', 'Women'].map((cat) => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  label="Caption (optional)"
                  value={captionsByTask[task.id] || ''}
                  onChange={(e) => setCaptionsByTask((prev) => ({ ...prev, [task.id]: e.target.value }))}
                  fullWidth
                />
              </Stack>

              {/* Upload button + progress */}
              <Box>
                <Button
                  variant="contained"
                  onClick={() => uploadSubmission(task)}
                  disabled={!filesByTask[task.id] || uploadingTaskId === task.id}
                  fullWidth={isMobile}
                  sx={{ textTransform: 'none', fontWeight: 900 }}
                >
                  {uploadingTaskId === task.id
                    ? `Uploading… ${uploadProgress > 0 ? `${uploadProgress}%` : ''}`
                    : 'Upload Performance Video'}
                </Button>
                {uploadingTaskId === task.id && (
                  <Box sx={{ mt: 1 }}>
                    <LinearProgress
                      variant={uploadProgress > 0 ? 'determinate' : 'indeterminate'}
                      value={uploadProgress}
                      sx={{ borderRadius: 1, height: 6 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      {uploadProgress < 100
                        ? `Uploading video… ${uploadProgress}% — please wait, do not close this page`
                        : 'Processing…'}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Stack>
          </Box>
        )}
      </Box>
    );
  };

  const renderSubmissionsView = () => {
    if (mySubmissions.length === 0) {
      return <Alert severity="info">No submissions found for this contest format yet.</Alert>;
    }
    return (
      <TableContainer sx={{ overflowX: 'auto', borderRadius: 2, boxShadow: 1 }}>
        <Table size="small" sx={{ minWidth: 480 }}>
          <TableHead sx={{ bgcolor: '#f1f5f9' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 800 }}>Contest</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Format</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Score</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Rating</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Submitted</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mySubmissions.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell sx={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <Tooltip title={row.taskTitle}><span>{row.taskTitle}</span></Tooltip>
                </TableCell>
                <TableCell>{getContestCategoryLabel(row.taskCategory)}</TableCell>
                <TableCell>
                  <Chip
                    label={row.status}
                    size="small"
                    color={row.status === 'approved' ? 'success' : row.status === 'rejected' ? 'error' : 'default'}
                    sx={{ fontWeight: 700, textTransform: 'capitalize' }}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{row.score ?? '—'}</TableCell>
                <TableCell>{row.rating != null ? `${row.rating}/5` : '—'}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {row.createdAt ? new Date(row.createdAt).toLocaleDateString() : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderLeaderboardView = () => {
    if (leaderboardRows.length === 0) {
      return <Alert severity="info">No approved leaderboard entries for this contest format yet.</Alert>;
    }
    return (
      <TableContainer sx={{ overflowX: 'auto', borderRadius: 2, boxShadow: 1 }}>
        <Table size="small" sx={{ minWidth: 420 }}>
          <TableHead sx={{ bgcolor: '#fef9c3' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 800, width: 36 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Player</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Contest</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Category</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Score</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Rating</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {leaderboardRows.map((row, index) => (
              <TableRow key={row.id} hover sx={{ bgcolor: index === 0 ? '#fffbeb' : undefined }}>
                <TableCell sx={{ fontWeight: 800, color: index < 3 ? '#d97706' : undefined }}>
                  {index + 1}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{(row as any).participantName || 'Player'}</TableCell>
                <TableCell sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <Tooltip title={row.taskTitle}><span>{row.taskTitle}</span></Tooltip>
                </TableCell>
                <TableCell>{(row as any).contestCategory || getContestCategoryLabel(row.taskCategory)}</TableCell>
                <TableCell sx={{ fontWeight: 800, color: '#0f4c81' }}>{row.score ?? '—'}</TableCell>
                <TableCell>{row.rating != null ? `${row.rating}/5` : '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderBrowseView = () => (
    <>
      {categoryFilter === 'all' && (
        <FeaturedSmashContest
          sportName={sport}
          onViewResults={() => switchView('submissions')}
          onViewLeaderboard={() => switchView('leaderboard')}
          onSubmit={() => {
            const firstTask = filteredTasks[0];
            if (firstTask) {
              setExpandedUploadTaskId(firstTask.id);
              taskRefs.current[firstTask.id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }}
        />
      )}

      {filteredTasks.length === 0 ? (
        <Alert severity="info">No active contests published for this format yet.</Alert>
      ) : (
        <>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f4c81', mb: 2 }}>
            Published Contest List
          </Typography>
          <Grid container spacing={2}>
            {filteredTasks.map((task) => (
              <Grid item xs={12} key={task.id}>
                <Box ref={(node) => { taskRefs.current[task.id] = node; }}>
                  <ContestTaskCard
                    task={task}
                    highlighted={highlightTaskId === task.id}
                    uploadSection={renderUploadSection(task)}
                    onViewResults={() => switchView('submissions', task.id)}
                    onViewLeaderboard={() => switchView('leaderboard', task.id)}
                    onSubmit={() => {
                      setExpandedUploadTaskId(task.id);
                      taskRefs.current[task.id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    submitLabel="Submit Performance"
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </>
  );

  return (
    <Box>
      {!hideHeader && <ContestHubHeader sportName={sport} />}
      {!hideHeader && <ContestSponsorBanner sportName={sport} />}

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : view === 'submissions' ? (
        renderSubmissionsView()
      ) : view === 'leaderboard' ? (
        renderLeaderboardView()
      ) : (
        renderBrowseView()
      )}
    </Box>
  );
};

export default ContestTasksPage;
